import { useState, useEffect } from 'react'
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Select } from 'antd'
import type { MenuProps } from 'antd'
import {
  DashboardOutlined,
  ProjectOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  ScheduleOutlined,
  PlayCircleOutlined,
  ApiOutlined,
  RobotOutlined,
  BarChartOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
  BulbOutlined,
  UnorderedListOutlined,
  CheckCircleOutlined,
  BranchesOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  SunOutlined,
  MoonOutlined,
  ToolOutlined,
  AimOutlined,
  BgColorsOutlined,
  ClusterOutlined,
  DeploymentUnitOutlined,
  TeamOutlined,
  BookOutlined,
  DatabaseOutlined,
  FileSearchOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useProjectStore } from '@/stores/project'
import { useThemeStore } from '@/stores/theme'
import './aurora-layout.css'

const { Header, Sider, Content } = Layout
const { Text } = Typography

/** 只作为分组容器的 key（无对应路由，不应触发导航） */
const GROUP_KEYS = ['/function-tests', '/api-test-group', '/ui-automation-group', '/test-plans-group', '/tools-group']

/** 根据当前路径推导应展开的一级菜单 key */
function getOpenKeys(pathname: string): string[] {
  if (pathname.startsWith('/ai-test-cases') || pathname.startsWith('/test-cases')) return ['/function-tests']
  if (pathname.startsWith('/api-test')) return ['/api-test-group']
  if (pathname.startsWith('/ui-automation')) return ['/ui-automation-group']
  if (pathname.startsWith('/timers') || pathname.startsWith('/timer-runs')) return ['/test-plans-group']
  if (pathname.startsWith('/tools/')) return ['/tools-group']
  if (pathname.startsWith('/settings')) return ['/settings']
  return []
}

/** 侧边栏菜单项 */
const menuItems: MenuProps['items'] = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台' },
  { key: '/projects', icon: <ProjectOutlined />, label: '项目管理' },
  { key: '/environments', icon: <CloudServerOutlined />, label: '环境管理' },
  { key: '/knowledge-base', icon: <BookOutlined />, label: '知识库' },
  {
    key: '/function-tests',
    icon: <ThunderboltOutlined />,
    label: '功能测试',
    children: [
      { key: '/ai-test-cases', icon: <ExperimentOutlined />, label: 'AI用例生成' },
      { key: '/test-cases', icon: <FileTextOutlined />, label: '测试用例' },
    ],
  },
  {
    key: '/api-test-group',
    icon: <ApiOutlined />,
    label: '接口测试',
    children: [
      { key: '/api-test', icon: <UnorderedListOutlined />, label: '接口列表' },
      { key: '/api-test/scenarios', icon: <BranchesOutlined />, label: '场景编排' },
      { key: '/api-test/mock', icon: <CloudServerOutlined />, label: 'Mock服务' },
    ],
  },
  {
    key: '/ui-automation-group',
    icon: <RobotOutlined />,
    label: 'UI 自动化',
    children: [
      { key: '/ui-automation/elements', icon: <BulbOutlined />, label: '元素库' },
      { key: '/ui-automation', icon: <AppstoreOutlined />, label: '用例管理' },
      { key: '/ui-automation/executions', icon: <PlayCircleOutlined />, label: '执行中心' },
      { key: '/ui-automation/dashboard', icon: <LineChartOutlined />, label: '趋势看板' },
      { key: '/ui-automation/healing', icon: <ThunderboltOutlined />, label: '自愈与缓存' },
      { key: '/ui-automation/reviews', icon: <CheckCircleOutlined />, label: '断言复核' },
    ],
  },
  {
    key: '/test-plans-group',
    icon: <ScheduleOutlined />,
    label: '测试计划',
    children: [
      { key: '/timers', icon: <ClockCircleOutlined />, label: '定时器' },
      { key: '/timer-runs', icon: <PlayCircleOutlined />, label: '定时器执行记录' },
    ],
  },
  { key: '/reports', icon: <BarChartOutlined />, label: '测试报告' },
  {
    key: '/tools-group',
    icon: <ToolOutlined />,
    label: '辅助工具',
    children: [
      { key: '/tools/capture', icon: <AimOutlined />, label: '抓包' },
      { key: '/tools/data-factory', icon: <DatabaseOutlined />, label: '数据构造' },
      { key: '/tools/payload-debug', icon: <FileSearchOutlined />, label: '报文调试' },
      { key: '/tools/log-text', icon: <FileTextOutlined />, label: '日志与文本' },
    ],
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: '系统配置',
    children: [
      { key: '/settings/theme', icon: <BgColorsOutlined />, label: '页面主题' },
      { key: '/settings/llm', icon: <RobotOutlined />, label: 'AI配置' },
      { key: '/settings/services', icon: <ClusterOutlined />, label: '服务管理' },
      { key: '/settings/drivers', icon: <DeploymentUnitOutlined />, label: '驱动管理' },
      { key: '/settings/ui-module', icon: <AppstoreOutlined />, label: 'UI模块设置' },
      { key: '/settings/users', icon: <TeamOutlined />, label: '用户管理' },
    ],
  },
]

/** 主布局组件 */
export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [openKeys, setOpenKeys] = useState<string[]>(() => getOpenKeys(location.pathname))
  const { user, logout } = useAuthStore()
  const { currentProject, projectList, setCurrentProject, fetchProjects } = useProjectStore()
  const { isDark, toggleDark } = useThemeStore()

  /** 路由变化时自动展开对应的子菜单 */
  useEffect(() => {
    const newKeys = getOpenKeys(location.pathname)
    setOpenKeys((prev) => {
      const merged = [...new Set([...prev, ...newKeys])]
      return merged.length !== prev.length || merged.some((k, i) => k !== prev[i]) ? merged : prev
    })
  }, [location.pathname])

  /** 页面挂载时加载项目列表；路由变化时若列表为空则重试 */
  useEffect(() => {
    if (projectList.length === 0) {
      fetchProjects()
    }
  }, [location.pathname, projectList.length, fetchProjects])

  /** 用户下拉菜单 */
  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '个人中心',
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        danger: true,
      },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'profile') {
        navigate('/profile')
      } else if (key === 'logout') {
        logout()
        navigate('/login')
      }
    },
  }

  return (
    <Layout className="aurora-root" style={{ minHeight: '100vh' }}>
      {/* 侧边栏 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        className="aurora-sider"
        style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* Logo 区域（固定，不参与滚动） */}
        <div className="sidebar-logo" style={{ flexShrink: 0 }}>
          <div className="sidebar-logo-icon">
            <img src="/logo.png" alt="AuroraTest" style={{ width: 36, height: 36, borderRadius: 8 }} />
          </div>
          {!collapsed && (
            <div>
              <div className="sidebar-logo-text">极光测试笔记</div>
              <div className="sidebar-logo-version">AuroraTest</div>
            </div>
          )}
        </div>

        {/* 菜单（独立纵向滚动，细窄滚动条见 aurora-layout.css） */}
        <div className="sider-menu-scroll">
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            openKeys={collapsed ? [] : openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            items={menuItems}
            onClick={({ key }) => {
              if (!GROUP_KEYS.includes(key)) navigate(key)
            }}
            style={{
              background: 'transparent',
              borderRight: 0,
              marginTop: 8,
            }}
          />
        </div>
      </Sider>

      <Layout
        style={{
          background: 'var(--bg)',
          minHeight: 0,
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {/* 顶部栏 */}
        <Header
          className="aurora-topbar"
          style={{
            background: 'var(--header-bg)',
            backdropFilter: 'blur(12px)',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--border)',
            height: 56,
            lineHeight: '56px',
            flexShrink: 0,
          }}
        >
          {/* 左侧：折叠按钮 + 项目切换 */}
          <Space size={16}>
            <div
              onClick={() => setCollapsed(!collapsed)}
              style={{
                cursor: 'pointer',
                fontSize: 16,
                color: 'var(--text-lighter)',
                transition: 'color 0.15s',
              }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 14, color: 'var(--text-lighter)' }}>当前项目：</Text>
              <Select
                value={currentProject?.id}
                onChange={(id) => {
                  const p = projectList.find((item) => item.id === id)
                  if (p) setCurrentProject(p)
                }}
                options={projectList.map((p) => ({ value: p.id, label: p.name }))}
                style={{ width: 200 }}
                size="middle"
                placeholder="请选择项目"
                listItemHeight={32}
              />
            </div>
          </Space>

          {/* 右侧用户区 */}
          <Space size={16}>
            {/* 深色/浅色切换 */}
            <div
              className="theme-toggle-btn"
              onClick={toggleDark}
              title={isDark ? '切换到浅色模式' : '切换到深色模式'}
            >
              {isDark ? <SunOutlined /> : <MoonOutlined />}
            </div>

            {/* 首页按钮 */}
            <Space
              style={{
                cursor: 'pointer',
                color: 'var(--primary)',
                fontSize: 13,
              }}
              onClick={() => navigate('/dashboard')}
            >
              <HomeOutlined />
              <span>首页</span>
            </Space>

            {/* 用户头像 */}
            <Dropdown menu={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }} align="center">
                <Avatar
                  size={34}
                  icon={<UserOutlined />}
                  src={user?.avatar || undefined}
                  style={{
                    backgroundColor: 'var(--primary)',
                    verticalAlign: 'middle',
                  }}
                />
                <div style={{ lineHeight: 1.3 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                    {user?.nickname || user?.username}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-lighter)' }}>
                    {(() => {
                      const ROLE_LABELS: Record<number, string> = { 1: '管理员', 2: '开发', 3: '测试', 4: '产品' }
                      return ROLE_LABELS[user?.role as number] || user?.role
                    })()}
                  </div>
                </div>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* 内容区 */}
        <Content
          style={{
            margin: 0,
            padding: 20,
            background: 'var(--bg)',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}
